import _asyncToGenerator from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _regeneratorRuntime from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useQuery } from '@apollo/client';
import { Editable, EditableInput, EditablePreview, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, Tooltip } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import * as R from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import API, { apiWithRetry } from 'src/api/API';
import { BranchVisitsByClientIDQueryGQL } from 'src/api/BranchVisit';
import { useUserByRoles } from 'src/api/UserApi';
import { useThrottleAsync } from 'src/api/useThrottleAsync';
import { BranchConstants } from 'src/constants';
import { getTherapyStageOptions, getTherapyTypeOptions, getTherapyTypeText, getVisitPurposeOptions, getVisitPurposeText } from 'src/constants/booking';
import { useUserDataContextProvider } from 'src/context/UserDataContextProvider';
import useStableValue from 'src/hooks/useStableValue';
import { BranchVisitStates, RoleType } from 'src/types';
import { pluckDistinctValues, utcDateString2localDateString } from 'src/utils';
import { getServiceNameList } from 'src/utils/booking';
import { getUserOptions } from 'src/utils/order';
import { getConsultationTreatmentWording } from 'src/utils/wording';
import DebouncedInput from './DebouncedInput';
import { getUserColumnDef } from './Table/Columns/BookingColumns';
import { CrudTable } from './Table/CrudTable';
import { filterFnCollection } from './Table/Filter/filterFnCollection';
import { renderTooltipCell } from './Table/TableCell';
import { Select } from './common/Form/Select';
import Loading from './common/Loading';
import TitleHeader from './common/TitleHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var EditableAppointmentRecordModal = function EditableAppointmentRecordModal(_ref) {
  var _ref$client = _ref.client,
      client = _ref$client === void 0 ? {} : _ref$client,
      onClose = _ref.onClose;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useUserDataContextPr = useUserDataContextProvider(),
      can = _useUserDataContextPr.can;

  var clientName = client.real_name,
      clientID = client.client_id;

  var _useState = useState(),
      updatedTime = _useState[0],
      setUpdatedTime = _useState[1];

  var _useQuery = useQuery(BranchVisitsByClientIDQueryGQL, {
    variables: {
      booking_source_names: BranchConstants.BOOKING_SOURCE_NAMES,
      client_id: clientID
    },
    onCompleted: function onCompleted() {
      setUpdatedTime(new Date());
    }
  }),
      data = _useQuery.data,
      loading = _useQuery.loading,
      refetch = _useQuery.refetch;

  var branchVisits = useMemo(function () {
    var _data$branch_visits$m, _data$branch_visits;

    return (_data$branch_visits$m = data === null || data === void 0 ? void 0 : (_data$branch_visits = data.branch_visits) === null || _data$branch_visits === void 0 ? void 0 : _data$branch_visits.map(function (branchVisit) {
      var _branchVisit$data$vis, _branchVisit$data, _branchVisit$data$vis2, _branchVisit$data2;

      return _objectSpread(_objectSpread(_objectSpread({}, branchVisit), branchVisit === null || branchVisit === void 0 ? void 0 : branchVisit.data), {}, {
        // `visit_purpose` is the old field that stores a single selection as a string.
        // `visit_purposes` is a new field that stores multiple selection values as an array.
        // If `visit_purposes` is defined (even if it's an empty array), we use it as the primary source.
        // If `visit_purposes` is undefined, we fall back to `visit_purpose` and convert it into an array to ensure compatibility with the multi-select dropdown.
        visit_purposes: (_branchVisit$data$vis = branchVisit === null || branchVisit === void 0 ? void 0 : (_branchVisit$data = branchVisit.data) === null || _branchVisit$data === void 0 ? void 0 : _branchVisit$data.visit_purposes) !== null && _branchVisit$data$vis !== void 0 ? _branchVisit$data$vis : [].concat((_branchVisit$data$vis2 = branchVisit === null || branchVisit === void 0 ? void 0 : (_branchVisit$data2 = branchVisit.data) === null || _branchVisit$data2 === void 0 ? void 0 : _branchVisit$data2.visit_purpose) !== null && _branchVisit$data$vis2 !== void 0 ? _branchVisit$data$vis2 : [])
      });
    })) !== null && _data$branch_visits$m !== void 0 ? _data$branch_visits$m : [];
  }, [data]); // perform a deep comparison to update serviceNameList or providerList, avoiding unnecessary recalculations of columns.

  var allServiceNameList = useMemo(function () {
    return pluckDistinctValues(branchVisits, 'service_name');
  }, [branchVisits]);
  var allProviderList = useMemo(function () {
    return pluckDistinctValues(branchVisits, 'provider_name');
  }, [branchVisits]);
  var stableServiceNameList = useStableValue(allServiceNameList);
  var stableProviderList = useStableValue(allProviderList);

  var _useMemo = useMemo(function () {
    return {
      serviceNameList: BranchConstants.ENABLE_BOOKING_PRINCIPAL_GENERATE_FILTER_OPTIONS_BY_VALUES ? stableServiceNameList : getServiceNameList(),
      providerList: BranchConstants.ENABLE_BOOKING_SERVICE_NAME_GENERATE_FILTER_OPTIONS_BY_VALUES ? stableProviderList : BranchConstants.PROVIDER_LIST
    };
  }, [stableServiceNameList, stableProviderList]),
      serviceNameList = _useMemo.serviceNameList,
      providerList = _useMemo.providerList;

  var _useMemo2 = useMemo(function () {
    return {
      therapyTypeOptions: getTherapyTypeOptions(t),
      therapyStageOptions: getTherapyStageOptions(t),
      visitPurposeOptions: getVisitPurposeOptions(t)
    };
  }, [t]),
      therapyTypeOptions = _useMemo2.therapyTypeOptions,
      therapyStageOptions = _useMemo2.therapyStageOptions,
      visitPurposeOptions = _useMemo2.visitPurposeOptions;

  var users = useUserByRoles([RoleType.CLINIC_DOCTOR_ROLE, RoleType.CLINIC_DIETICIAN_ROLE, RoleType.CLINIC_SALESPERSON_ROLE]);

  var _useMemo3 = useMemo(function () {
    var _users$data = users.data,
        _users$data$RoleType$ = _users$data[RoleType.CLINIC_DOCTOR_ROLE],
        doctors = _users$data$RoleType$ === void 0 ? [] : _users$data$RoleType$,
        _users$data$RoleType$2 = _users$data[RoleType.CLINIC_DIETICIAN_ROLE],
        dieticians = _users$data$RoleType$2 === void 0 ? [] : _users$data$RoleType$2,
        _users$data$RoleType$3 = _users$data[RoleType.CLINIC_SALESPERSON_ROLE],
        salespeople = _users$data$RoleType$3 === void 0 ? [] : _users$data$RoleType$3;
    return {
      doctors: getUserOptions(doctors),
      dieticians: getUserOptions(dieticians),
      salespeople: getUserOptions(salespeople)
    };
  }, [users.data]),
      doctors = _useMemo3.doctors,
      dieticians = _useMemo3.dieticians,
      salespeople = _useMemo3.salespeople;

  var getTableDataBulkUpdates = useCallback(function (changes) {
    var records = Object.entries(changes).map(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
          rowIndex = _ref3[0],
          data = _ref3[1];

      var original = branchVisits[parseInt(rowIndex)];
      var id = original.id;
      return {
        id: id,
        changed: data
      };
    });
    var promises = [];
    records.forEach(function (_ref4) {
      var id = _ref4.id,
          changed = _ref4.changed;

      if (!R.isEmpty(changed)) {
        promises.push(apiWithRetry.updateBranchVisit(id, changed));
      }
    });
    return {
      promises: promises,
      records: records
    };
  }, [branchVisits]);
  var refetchAndUpdateTime = useCallback( /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(options) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return refetch(options);

            case 2:
              setUpdatedTime(new Date());

            case 3:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref5.apply(this, arguments);
    };
  }(), [refetch]);
  var update = useCallback(function (payloads, controller) {
    var mergedPayloads = Array.from(payloads.reduce(function (map, payload) {
      if (map.has(payload.id)) {
        var existingPayload = map.get(payload.id);
        map.set(payload.id, _objectSpread(_objectSpread({}, existingPayload), payload));
      } else {
        map.set(payload.id, payload);
      }

      return map;
    }, new Map())).map(function (_ref6) {
      var _ref7 = _slicedToArray(_ref6, 2),
          payload = _ref7[1];

      return payload;
    });
    return Promise.all(mergedPayloads.map(function (payload) {
      return API.updateBranchVisit(payload.id, payload, {
        signal: controller.signal
      });
    }));
  }, []);

  var _useThrottleAsync = useThrottleAsync({
    update: update,
    onUpdated: refetchAndUpdateTime
  }),
      queue$ = _useThrottleAsync.queue$;

  var branchVisitChangeHandler = useCallback(function (key, id) {
    return function (value) {
      if (!id) {
        return;
      }

      queue$.next(_defineProperty({
        id: id
      }, key, value));
    };
  }, [queue$]);
  var columns = useMemo(function () {
    return [{
      header: t('client:normal.appointmentDate', '預約/診療日期'),
      id: 'time',
      accessorKey: 'time',
      size: 160,
      cell: function cell(info) {
        var value = info.getValue();
        return /*#__PURE__*/_jsx(Text, {
          e2eid: "components_EditableAppointmentRecordModal_Text",
          children: utcDateString2localDateString(value, 'YYYY-MM-DD HH:mm')
        });
      }
    }, {
      header: t(getConsultationTreatmentWording('bookingItemText'), '回診項目'),
      id: 'service_name',
      accessorKey: 'service_name',
      size: 160,
      cell: renderTooltipCell,
      filterFn: filterFnCollection.text,
      meta: {
        filterVariant: 'select',
        selectFilterProps: {
          options: serviceNameList,
          optionWithTooltip: true
        }
      }
    }, {
      header: t(getConsultationTreatmentWording('bookingPrincipalText'), '負責人'),
      id: 'provider_name',
      accessorKey: 'provider_name',
      size: 160,
      cell: renderTooltipCell,
      filterFn: filterFnCollection.text,
      meta: {
        filterVariant: 'select',
        selectFilterProps: {
          options: providerList,
          optionWithTooltip: true
        }
      }
    }, {
      header: t('client:number', '是否報到'),
      id: 'state',
      accessorKey: 'state',
      size: 96,
      cell: function cell(info) {
        var state = info.getValue();
        return /*#__PURE__*/_jsx(Text, {
          e2eid: "components_EditableAppointmentRecordModal_Text_1",
          children: state === BranchVisitStates.checked_in ? t('client:normal.onBoarded', '報到') : t('client:normal.notOnboard', '未報到')
        });
      },
      filterFn: filterFnCollection.text
    }, {
      header: t('booking:therapyType', '療程種類'),
      id: 'therapy_type',
      accessorKey: 'therapy_type',
      size: 160,
      cell: function cell(info) {
        var value = getTherapyTypeText(t, info.getValue());
        return /*#__PURE__*/_jsx(Tooltip, {
          label: value,
          e2eid: "components_EditableAppointmentRecordModal_Tooltip",
          children: /*#__PURE__*/_jsx(Text, {
            isTruncated: true,
            e2eid: "components_EditableAppointmentRecordModal_Text",
            children: value
          })
        });
      },
      filterFn: filterFnCollection.text,
      meta: {
        editable: {
          renderEditCell: function renderEditCell(value, setValue, isDisabled) {
            return /*#__PURE__*/_jsx(Select, {
              value: therapyTypeOptions.find(function (option) {
                return option.value === value;
              }),
              onChange: function onChange(option) {
                return setValue(option.value);
              },
              options: therapyTypeOptions,
              placeholder: t('common:form.pleaseSelect', '請選擇'),
              isDisabled: isDisabled,
              e2eid: "components_EditableAppointmentRecordModal_Select"
            });
          }
        },
        bulkUpdate: {
          options: therapyTypeOptions
        },
        filterVariant: 'select',
        selectFilterProps: {
          options: therapyTypeOptions
        }
      }
    }, {
      header: t('booking:therapyStage', '療程階段'),
      id: 'therapy_stage',
      accessorKey: 'therapy_stage',
      size: 160,
      filterFn: filterFnCollection.text,
      cell: renderTooltipCell,
      meta: {
        editable: {
          renderEditCell: function renderEditCell(value, setValue, isDisabled) {
            return /*#__PURE__*/_jsx(Select, {
              value: therapyStageOptions.find(function (option) {
                return option.value === value;
              }),
              onChange: function onChange(option) {
                return setValue(option.value);
              },
              options: therapyStageOptions,
              placeholder: t('common:form.pleaseSelect', '請選擇'),
              isDisabled: isDisabled,
              e2eid: "components_EditableAppointmentRecordModal_Select_5"
            });
          }
        },
        bulkUpdate: {
          options: therapyStageOptions
        },
        filterVariant: 'select',
        selectFilterProps: {
          options: therapyStageOptions
        }
      }
    }, {
      header: t('booking:actual_project', '實際項目'),
      id: 'visit_purposes',
      accessorKey: 'visit_purposes',
      size: 160,
      cell: function cell(info) {
        var value = info.getValue().map(function (visitPurposeValue) {
          return getVisitPurposeText(t, visitPurposeValue);
        }).join(',');
        return /*#__PURE__*/_jsx(Tooltip, {
          label: value,
          e2eid: "components_EditableAppointmentRecordModal_Tooltip",
          children: /*#__PURE__*/_jsx(Text, {
            isTruncated: true,
            e2eid: "components_EditableAppointmentRecordModal_Text",
            children: value
          })
        });
      },
      filterFn: filterFnCollection.text,
      meta: {
        editable: {
          renderEditCell: function renderEditCell(value, setValue, isDisabled) {
            return /*#__PURE__*/_jsx(Select, {
              optionWithTooltip: true,
              value: visitPurposeOptions.filter(function (option) {
                return value.includes(option.value);
              }),
              onChange: function onChange(options) {
                return setValue(options.map(function (option) {
                  return option.value;
                }));
              },
              options: visitPurposeOptions,
              placeholder: t('common:form.pleaseSelect', '請選擇'),
              isMulti: true,
              isDisabled: !can({
                type: 'data',
                subject: 'branch_visit',
                action: 'update',
                property: 'visit_purposes'
              }) || isDisabled,
              isClearable: false,
              closeMenuOnSelect: false,
              showTooltip: true,
              e2eid: "components_EditableAppointmentRecordModal_Select"
            });
          }
        },
        getDisplayValue: function getDisplayValue(values) {
          return values.join(',');
        },
        bulkUpdate: {
          isMulti: true,
          options: visitPurposeOptions
        },
        filterVariant: 'select',
        selectFilterProps: {
          options: visitPurposeOptions,
          optionWithTooltip: true
        }
      }
    }, {
      header: t('common:normal.remark', '備註'),
      id: 'note',
      accessorKey: 'note',
      size: 160,
      cell: function cell(info) {
        return /*#__PURE__*/_jsxs(Editable, {
          defaultValue: info.getValue(),
          onSubmit: function onSubmit(value) {
            return branchVisitChangeHandler('note', info.row.original.id)(value);
          },
          placeholder: "-",
          e2eid: "components_EditableAppointmentRecordModal_Editable",
          children: [/*#__PURE__*/_jsx(Tooltip, {
            label: info.getValue(),
            e2eid: "components_EditableAppointmentRecordModal_Tooltip",
            children: /*#__PURE__*/_jsx(EditablePreview, {
              w: "full",
              isTruncated: true,
              e2eid: "components_EditableAppointmentRecordModal_EditablePreview"
            })
          }), /*#__PURE__*/_jsx(Input, {
            as: EditableInput,
            e2eid: "components_EditableAppointmentRecordModal_Input"
          })]
        }, info.getValue());
      },
      meta: {
        editable: {
          renderEditCell: function renderEditCell(value, setValue, isDisabled) {
            return /*#__PURE__*/_jsx(DebouncedInput, {
              value: value,
              onChange: setValue,
              placeholder: "-",
              isDisabled: isDisabled,
              backgroundColor: "white",
              e2eid: "components_EditableAppointmentRecordModal_DebouncedInput"
            });
          }
        }
      }
    }, getUserColumnDef('doctor_id', t('common:normal.response_doctor', '負責醫師'), t, doctors), getUserColumnDef('master_dietician_id', t('common:normal.chief_dietician', '主營養師'), t, dieticians), getUserColumnDef('master_dietician_delegate_id', t('common:normal.chief_dietician_agent', '主營養師（代）'), t, dieticians), getUserColumnDef('salesperson_id', t('common:normal.health_secretary', '健康秘書'), t, salespeople), getUserColumnDef('salesperson_delegate_id', t('common:normal.health_secretary_agent', '健康秘書（代）'), t, salespeople)];
  }, [t, serviceNameList, providerList, therapyTypeOptions, therapyStageOptions, visitPurposeOptions, doctors, dieticians, salespeople, can, branchVisitChangeHandler]);
  return /*#__PURE__*/_jsxs(Modal, {
    isOpen: true,
    onClose: onClose,
    isCentered: true,
    e2eid: "components_EditableAppointmentRecordModal_Modal",
    children: [/*#__PURE__*/_jsx(ModalOverlay, {
      e2eid: "components_EditableAppointmentRecordModal_ModalOverlay"
    }), /*#__PURE__*/_jsxs(ModalContent, {
      maxW: "80vw",
      e2eid: "components_EditableAppointmentRecordModal_ModalContent",
      children: [/*#__PURE__*/_jsx(ModalHeader, {
        pb: 0,
        e2eid: "components_EditableAppointmentRecordModal_ModalHeader",
        children: /*#__PURE__*/_jsx(TitleHeader, {
          e2eid: "components_EditableAppointmentRecordModal_TitleHeader",
          children: "".concat(clientName, " ").concat(t('client:normal.appointmentProcess', '預約歷程'))
        })
      }), /*#__PURE__*/_jsx(ModalCloseButton, {
        e2eid: "components_EditableAppointmentRecordModal_ModalCloseButton"
      }), /*#__PURE__*/_jsx(ModalBody, {
        e2eid: "components_EditableAppointmentRecordModal_ModalBody",
        children: loading ? /*#__PURE__*/_jsx(Loading, {
          minHeight: 600,
          e2eid: "components_EditableAppointmentRecordModal_Loading"
        }) : /*#__PURE__*/_jsx(CrudTable, {
          maxH: "max(50vh, 600px)",
          data: branchVisits,
          columns: columns,
          columnVisibility: {
            provider_name: can({
              type: 'data',
              subject: 'branch_visit',
              action: 'read',
              property: 'provider_name'
            }),
            state: can({
              type: 'data',
              subject: 'branch_visit',
              action: 'read',
              property: 'state'
            }),
            therapy_type: can({
              type: 'data',
              subject: 'branch_visit',
              action: 'read',
              property: 'therapy_type'
            }),
            therapy_stage: can({
              type: 'data',
              subject: 'branch_visit',
              action: 'read',
              property: 'therapy_stage'
            })
          },
          handlers: {
            handleUpdatesAllSuccess: function () {
              var _handleUpdatesAllSuccess = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
                return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                  while (1) {
                    switch (_context2.prev = _context2.next) {
                      case 0:
                        _context2.next = 2;
                        return refetchAndUpdateTime();

                      case 2:
                        return _context2.abrupt("return", true);

                      case 3:
                      case "end":
                        return _context2.stop();
                    }
                  }
                }, _callee2);
              }));

              function handleUpdatesAllSuccess() {
                return _handleUpdatesAllSuccess.apply(this, arguments);
              }

              return handleUpdatesAllSuccess;
            }(),
            handleUpdatesPartialSuccess: function () {
              var _handleUpdatesPartialSuccess = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
                return _regeneratorRuntime.wrap(function _callee3$(_context3) {
                  while (1) {
                    switch (_context3.prev = _context3.next) {
                      case 0:
                        _context3.next = 2;
                        return refetchAndUpdateTime();

                      case 2:
                        return _context3.abrupt("return", true);

                      case 3:
                      case "end":
                        return _context3.stop();
                    }
                  }
                }, _callee3);
              }));

              function handleUpdatesPartialSuccess() {
                return _handleUpdatesPartialSuccess.apply(this, arguments);
              }

              return handleUpdatesPartialSuccess;
            }(),
            handleUpdatesAllFailure: function handleUpdatesAllFailure() {
              return false;
            }
          },
          getBulkUpdates: getTableDataBulkUpdates,
          pageSize: 50,
          stickyColumns: {
            left: [0]
          },
          customToolbar: /*#__PURE__*/_jsxs(Text, {
            fontSize: "md",
            py: "1",
            align: "right",
            e2eid: "components_EditableAppointmentRecordModal_Text",
            children: [t('booking:systemLastUpdate', '系統最後更新時間'), "\uFF1A", dayjs(updatedTime).format('YYYY-MM-DD HH:mm:ss')]
          }),
          e2eid: "components_EditableAppointmentRecordModal_CrudTable"
        })
      })]
    })]
  });
};

export default EditableAppointmentRecordModal;